import BaseApiService from './BaseApiService.js';

class CourseService extends BaseApiService {

  // ✅ Create a new course
  async createCourse(courseData) {
    // Validate required fields
    if (!courseData.courseName || !courseData.courseName.trim()) {
      throw new Error('Course name is required');
    }
    if (!courseData.courseCode || !courseData.courseCode.trim()) {
      throw new Error('Course code is required');
    }

    // Clean up payload - remove orgId as backend extracts it from token
    const cleanedData = {
      courseName: courseData.courseName.trim(),
      courseCode: courseData.courseCode.trim(),
      duration: Number(courseData.duration) || 3,
      departmentId: courseData.departmentId ? Number(courseData.departmentId) : null,
      department: courseData.departmentId ? { departmentId: Number(courseData.departmentId) } : null,
      status: courseData.status || 'ACTIVE'
    };

    console.log('📤 Creating course with payload:', cleanedData);
    console.log('ℹ️ OrgId will be extracted from authentication token');
    
    try {
      const response = await this.makeRequest('/api/courses/create', {
        method: 'POST',
        body: JSON.stringify(cleanedData),
      });
      console.log('✅ Course created successfully:', response);
      return response;
    } catch (error) {
      console.error('❌ Failed to create course:', error);
      throw error;
    }
  }

  // ✅ Get all courses
  async getAllCourses() {
    return this.makeRequest('/api/courses/all');
  }

  // ✅ Get courses by organization
  async getCoursesByOrg(orgId) {
    if (!orgId) {
      console.warn('⚠️ OrgId is null or undefined, cannot fetch courses');
      return [];
    }
    try {
      console.log(`🚀 Fetching all courses and filtering by orgId: ${orgId} (Type: ${typeof orgId})`);
      // Fetch all courses and filter by orgId on frontend
      const response = await this.makeRequest('/api/courses/all', { 
        method: 'GET'
      });
      
      console.log('📥 Raw API response:', response);
      
      // Extract courses array from response
      let coursesArray = [];
      if (Array.isArray(response)) {
        coursesArray = response;
        console.log('✅ Response is direct array with', coursesArray.length, 'courses');
      } else if (response && response.courses && Array.isArray(response.courses)) {
        coursesArray = response.courses;
        console.log('✅ Response has courses property with', coursesArray.length, 'courses');
      } else if (response && Array.isArray(response.data)) {
        coursesArray = response.data;
        console.log('✅ Response has data property with', coursesArray.length, 'courses');
      } else {
        console.warn('⚠️ Unexpected response format:', response);
        return [];
      }
      
      // Filter courses by orgId (handle both numeric and string comparison)
      const numericOrgId = parseInt(orgId, 10);
      const filtered = coursesArray.filter(course => {
        // Try numeric comparison first, then string comparison
        const courseOrgId = course.orgId;
        const matches = (courseOrgId === numericOrgId) || 
                       (String(courseOrgId) === String(orgId));
        if (matches) {
          console.log(`✅ Course "${course.courseName}" matches orgId ${orgId}`);
        }
        return matches;
      });
      
      console.log(`📊 Filtered ${filtered.length} courses for orgId: ${orgId}`);
      return filtered;
    } catch (error) {
      console.error(`❌ Failed to fetch courses for org ${orgId}:`, error);
      throw error;
    }
  }

  // ✅ Get active courses by organization
  async getActiveCoursesByOrg(orgId) {
    if (!orgId) {
      console.warn('⚠️ OrgId is null or undefined, cannot fetch active courses');
      return [];
    }
    try {
      console.log(`🚀 Fetching all active courses and filtering by orgId: ${orgId}`);
      // Fetch all courses and filter by orgId and status on frontend
      const response = await this.makeRequest('/api/courses/all', { 
        method: 'GET'
      });
      
      console.log('📥 Raw API response:', response);
      
      // Extract courses array from response
      let coursesArray = [];
      if (Array.isArray(response)) {
        coursesArray = response;
      } else if (response && response.courses && Array.isArray(response.courses)) {
        coursesArray = response.courses;
      } else if (response && Array.isArray(response.data)) {
        coursesArray = response.data;
      } else {
        console.warn('⚠️ Unexpected response format:', response);
        return [];
      }
      
      // Filter courses by orgId and status
      const numericOrgId = parseInt(orgId, 10);
      const filtered = coursesArray.filter(course => {
        const courseOrgId = course.orgId;
        const orgMatch = (courseOrgId === numericOrgId) || 
                        (String(courseOrgId) === String(orgId));
        const statusMatch = course.status === 'ACTIVE';
        return orgMatch && statusMatch;
      });
      
      console.log(`📊 Filtered ${filtered.length} active courses for orgId: ${orgId}`);
      return filtered;
    } catch (error) {
      console.error(`❌ Failed to fetch active courses for org ${orgId}:`, error);
      throw error;
    }
  }

  // ✅ Get course by ID
  async getCourseById(courseId) {
    return this.makeRequest(`/api/courses/${courseId}`);
  }

  // ✅ Get course by code
  async getCourseByCode(courseCode) {
    return this.makeRequest(`/api/courses/code/${courseCode}`);
  }

  // ✅ Get courses by department
  async getCoursesByDepartment(departmentId) {
    return this.makeRequest(`/api/courses/department/${departmentId}`);
  }

  // ✅ Update course
  async updateCourse(courseId, courseData) {
    return this.makeRequest(`/api/courses/${courseId}`, {
      method: 'PUT',
      body: JSON.stringify(courseData),
    });
  }

  // ✅ Delete course
  async deleteCourse(courseId) {
    return this.makeRequest(`/api/courses/${courseId}`, {
      method: 'DELETE',
    });
  }

  // ✅ Deactivate course
  async deactivateCourse(courseId) {
    return this.makeRequest(`/api/courses/${courseId}/deactivate`, {
      method: 'PUT',
    });
  }

  // ✅ Activate course
  async activateCourse(courseId) {
    return this.makeRequest(`/api/courses/${courseId}/activate`, {
      method: 'PUT',
    });
  }

  // ✅ Search courses by name
  async searchCoursesByName(courseName) {
    return this.makeRequest(`/api/courses/search?courseName=${encodeURIComponent(courseName)}`);
  }

  // ✅ Get course count for organization
  async getCourseCount(orgId) {
    if (!orgId) return 0;
    const finalOrgId = /^\d+$/.test(orgId) ? parseInt(orgId, 10) : orgId;
    return this.makeRequest(`/api/courses/org/${finalOrgId}/count`);
  }

  // ✅ Get courses by duration
  async getCoursesByDuration(duration) {
    return this.makeRequest(`/api/courses/duration/${duration}`);
  }

  // ✅ Get courses by type
  async getCoursesByType(courseType) {
    return this.makeRequest(`/api/courses/type/${courseType}`);
  }

  // ✅ Get inactive courses
  async getInactiveCoursesByOrg(orgId) {
    if (!orgId) return [];
    const finalOrgId = /^\d+$/.test(orgId) ? parseInt(orgId, 10) : orgId;
    return this.makeRequest(`/api/courses/org/${finalOrgId}/inactive`);
  }
}

// ✅ Export single instance
const courseService = new CourseService();
export default courseService;
